import React, { createContext, forwardRef, useCallback, useContext, useEffect, useRef, } from 'react';
import Portal from '@polestar/component-warehouse-react/components/portal/portal';
import styled from '@emotion/styled';
import { m as motion, motionValue, useMotionValue } from 'framer-motion';
import { css } from '@emotion/react';
import { pointerOnlyStyle } from '@polestar/component-warehouse-react/helpers/accessibility';
import { useInteractionMode } from '@polestar/component-warehouse-react/contexts/interactionMode';
import { passiveEventOptions } from '@polestar/component-warehouse-react/helpers/supports';
import LayoutIndexes from '@polestar/component-warehouse-react/tokens/layout-indexes/LayoutIndexes';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
import useFullscreen from '@polestar/component-warehouse-react/hooks/useFullscreen';
import { useDirection } from '@polestar/component-warehouse-react/contexts/direction';
export const focusProviderSkipProps = {
    ['data-focus-skip']: true,
};
export const focusProviderOptions = ({ skip = false, skipParent = undefined, round = false, radius = null, spacing = 0, preventScroll = false, themeType, }) => ({
    ['data-focus-skip']: skip ? true : undefined,
    ['data-focus-skip-parent']: skipParent !== undefined ? skipParent : undefined,
    ['data-focus-radius']: round ? '50%' : radius || undefined,
    ['data-focus-spacing']: spacing ? spacing : undefined,
    ['data-focus-prevent-scroll']: preventScroll || undefined,
    ['data-focus-theme']: themeType ? themeType : undefined,
});
/**
 * The useFocusProvider hook can be used to size & position the focus border based on another element.
 * This is useful if it makes more sense to show the parent as focused or when you have hidden input that gets focused for example.
 *
 * The example below will show a larger focus border because it takes the h1 into account.
 *
 * ```jsx
 * import { useFocusProvider } from '@polestar/component-warehouse-react';
 *
 * const { focusParentProps, focusProps } = useFocusProvider();
 *
 * <div {...focusParentProps}>
 *   <h1>This is my title</h1>
 *   <button {...focusProps}>Focus will be on me</button>
 * </div>
 * ```
 *
 *
 * ## focusProviderOptions
 *
 * Some elements need to pass options to the focus border. You can use the `focusProviderOptions` for this.
 *
 * ```jsx
 * import { focusProviderOptions } from '@polestar/component-warehouse-react';
 *
 * <button {...focusProviderOptions({
 *   skip: true, // Like `focusProviderSkipProps`
 *   round: true, // Make the focus border a circle
 *   radius: '0.5rem', // Add a border radius
 *   spacing: 5, // Add extra spacing around in px
 *   preventScroll: true, // Prevent scrolling the element into view
 * })}>Focus will be on me</button>
 * ```
 * ## focusProviderSkipProps
 *
 * If you want to skip the focus border for a specific element, you can use the `focusProviderSkipProps`.
 * Be sure to implement your own :focus styling when you use this. We use this on most of the form elements because we already have the border.
 *
 * ```jsx
 * <button {...focusProviderSkipProps}>Focus will be hidden</button>
 * ```
 *
 * @storybook Hooks/useFocusProvider
 */
export const useFocusProvider = ({ id = '', radius, spacing, themeType, } = {}) => {
    const memoizedId = useSSRSafeId('focus', id);
    return {
        focusParentProps: {
            ['data-focus-identifier']: memoizedId,
        },
        focusProps: {
            ['data-focus-parent']: memoizedId,
            ['data-focus-radius']: radius || undefined,
            ['data-focus-spacing']: spacing || undefined,
            ['data-focus-theme']: themeType || undefined,
        },
    };
};
const FocusContext = createContext({
    width: motionValue(0),
    height: motionValue(0),
    top: motionValue(-10),
    left: motionValue(-10),
    borderRadius: motionValue('0%'),
    forceRender: () => null,
});
/**
 * @internal
 */
export const FocusProvider = ({ children }) => {
    const width = useMotionValue(0);
    const height = useMotionValue(0);
    const top = useMotionValue(-10);
    const left = useMotionValue(-10);
    const borderRadius = useMotionValue('0%');
    const themeType = useMotionValue(undefined);
    const internalRef = useRef(null);
    const { isKeyboardControlled } = useInteractionMode();
    const { isRTL } = useDirection();
    const forcedRenderFunction = useRef(() => null);
    const handleHide = useCallback(() => {
        width.set(0);
        height.set(0);
        top.set(-10);
        left.set(-10);
        borderRadius.set('0%');
        themeType.set(undefined);
    }, [width, height, top, left, borderRadius, themeType]);
    // Move this to context
    useEffect(() => {
        let timer;
        const createFocus = (delay = 50) => () => {
            var _a, _b, _c;
            const _activeElement = document.activeElement;
            clearTimeout(timer);
            if (!isKeyboardControlled) {
            }
            else if (_activeElement &&
                (_activeElement === (document === null || document === void 0 ? void 0 : document.body) ||
                    (((_a = _activeElement.dataset) === null || _a === void 0 ? void 0 : _a.focusSkip) === 'true' &&
                        (((_b = _activeElement.dataset) === null || _b === void 0 ? void 0 : _b.focusSkipParent) === undefined ||
                            ((_c = _activeElement.dataset) === null || _c === void 0 ? void 0 : _c.focusSkipParent) === 'false')))) {
                // blurred so hide the focus border
                handleHide();
            }
            else {
                const handleUpdate = () => {
                    var _a;
                    // gather size of PolestarApp because it might be embedded into a site and therefore not 100% wide
                    const dimensions = (_a = document.querySelector('div[data-version]')) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                    // if no width dimensions are available then the viewport will possibly 0x0 px (for example a testing framework)
                    if (dimensions === null || dimensions === void 0 ? void 0 : dimensions.width) {
                        const { focusRadius, focusSpacing: _spacing, focusParent, focusTheme, focusSkip, focusSkipParent, } = _activeElement.dataset;
                        const focusElement = 
                        // when using custom fake focus (like the AutocompleteField) the input field should not be focused so determine if the activeElement has a data-focus-skip attribute
                        !!focusSkip && focusSkipParent !== undefined
                            ? document.querySelector(`${focusSkipParent} [data-focus-identifier]`)
                            : focusParent
                                ? document.querySelector(`[data-focus-identifier="${focusParent}"]`)
                                : _activeElement;
                        const spacing = parseInt(_spacing || '0') || 0;
                        if (focusElement) {
                            const focusedElement = focusElement.getBoundingClientRect();
                            const currentScrollTop = (document.documentElement || document.body).scrollTop;
                            const currentScrollLeft = (document.documentElement || document.body).scrollLeft;
                            const currentLeft = focusedElement.left + currentScrollLeft - spacing;
                            width.set(focusedElement.width + spacing * 2);
                            height.set(focusedElement.height + spacing * 2);
                            top.set(focusedElement.top + currentScrollTop - spacing);
                            left.set(
                            // RTL flips the x-values because x=0 is the right side of the screen
                            isRTL
                                ? currentLeft - (((dimensions === null || dimensions === void 0 ? void 0 : dimensions.width) || 0) - focusedElement.width - spacing * 2)
                                : currentLeft);
                            borderRadius.set(focusRadius || '0%');
                            themeType.set(focusTheme || undefined);
                        }
                    }
                };
                forcedRenderFunction.current = handleUpdate;
                if (delay) {
                    timer = setTimeout(handleUpdate, delay);
                }
                else {
                    handleUpdate();
                }
            }
        };
        const handleFocus = createFocus(50);
        document.addEventListener('focusin', handleFocus, passiveEventOptions);
        window.addEventListener('blur', handleHide, passiveEventOptions);
        window.addEventListener('resize', handleFocus, passiveEventOptions);
        // if you make this direct (delay=0ms) then on Safari at the DropdownField it will focus on the first item (when going down from the last option) and it will show a focus "ring" at the top of the page.
        // therefore this delay is necessary also for key input
        document.addEventListener('keydown', handleFocus, passiveEventOptions);
        document.addEventListener('keyup', handleFocus, passiveEventOptions);
        document.addEventListener('scroll', handleFocus, passiveEventOptions);
        document.addEventListener('wheel', handleFocus, passiveEventOptions);
        return () => {
            document.removeEventListener('focusin', handleFocus);
            window.removeEventListener('blur', handleHide);
            window.removeEventListener('resize', handleFocus);
            document.removeEventListener('keydown', handleFocus);
            document.removeEventListener('keyup', handleFocus);
            document.removeEventListener('scroll', handleFocus);
            document.removeEventListener('wheel', handleFocus);
            forcedRenderFunction.current = null;
        };
    }, [width, height, top, left, isKeyboardControlled, borderRadius, handleHide, themeType, isRTL]);
    useEffect(() => {
        return themeType.on('change', (latestThemeType) => {
            if (internalRef === null || internalRef === void 0 ? void 0 : internalRef.current) {
                if (!latestThemeType) {
                    internalRef.current.removeAttribute('data-theme');
                }
                else {
                    internalRef.current.setAttribute('data-theme', latestThemeType);
                }
            }
        });
    }, [themeType]);
    const handleForceRender = () => {
        var _a;
        (_a = forcedRenderFunction.current) === null || _a === void 0 ? void 0 : _a.call(forcedRenderFunction);
    };
    return (React.createElement(FocusContext.Provider, { value: { width, height, top, left, borderRadius, forceRender: handleForceRender } },
        children,
        React.createElement(Portal, { isFocusContainer: true },
            React.createElement(FocusRing, { ref: internalRef, "aria-hidden": true }))));
};
/**
 * This hook only provides available functions to force a re-render of the focus border.
 */
export const useFocusRing = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { width, height, top, left, borderRadius, ...props } = useContext(FocusContext);
    return props;
};
const FocusRing = forwardRef((props, ref) => {
    const { width, height, top, left, borderRadius } = useContext(FocusContext);
    return (React.createElement(Ring, { ref: ref, ...props, style: {
            width,
            height,
            y: top,
            x: left,
            borderRadius,
        }, "aria-hidden": true }));
});
FocusRing.displayName = 'FocusBorder';
/**
 * @internal
 *
 * When an element is fullscreen, the normal focus ring is in a portal behind the fullscreen element.
 * This is an extra component that is rendered inside the fullscreen element and acts the same way as the normal focus ring.
 */
export const FullscreenFocusRing = forwardRef((props, ref) => {
    const [isFullscreen] = useFullscreen();
    return isFullscreen ? React.createElement(FocusRing, { ...props, ref: ref }) : null;
});
FullscreenFocusRing.displayName = 'FullscreenFocusRing';
const Ring = styled(motion.div)(({ theme }) => css `
    z-index: ${LayoutIndexes.popover + 1};
    pointer-events: none;
    position: absolute;
    inset-block-start: 0;
    inset-inline-start: 0;
    box-shadow:
      0 0 0 ${theme.polestarApp.focus.inner.width} ${theme.polestarApp.focus.inner.color},
      0 0 0 ${theme.polestarApp.focus.outer.width} ${theme.polestarApp.focus.outer.color};

    &[data-theme='light'] {
      box-shadow:
        0 0 0 ${theme.polestarApp.focus.inner.width} ${theme.polestarApp.focus.inner.color},
        0 0 0 ${theme.polestarApp.focus.outer.width} ${theme.polestarApp.focus.outer.color};
    }

    &[data-theme='dark'] {
      box-shadow:
        0 0 0 ${theme.polestarApp.focus.inner.width} ${theme.polestarApp.focus.outer.color},
        0 0 0 ${theme.polestarApp.focus.outer.width} ${theme.polestarApp.focus.inner.color};
    }

    ${pointerOnlyStyle} {
      display: none;
    }
  `);
